import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import './MarkdownRenderer.css';

// `breaks` (FE-5-19): treat a single newline as a line break, as chat text is
// written. Opt-in, so documents and other Markdown keep standard paragraphs.
const PLUGINS = [remarkGfm];
const PLUGINS_WITH_BREAKS = [remarkGfm, remarkBreaks];

const MarkdownRenderer = ({ content, className = '', breaks = false }) => {
  // Ensure content is a string
  const markdownContent = typeof content === 'string' ? content : String(content || '');

  return (
    <div className={`markdown-renderer ${className}`}>
      <ReactMarkdown
        remarkPlugins={breaks ? PLUGINS_WITH_BREAKS : PLUGINS}
        components={{
          // Custom link component - opens in new tab
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer" />
          ),
          // Custom code block component with syntax highlighting
          // In react-markdown v9+, 'inline' prop was removed. Check className instead.
          code: ({ node, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const isInline = !className;
            return !isInline && match ? (
              <SyntaxHighlighter
                style={tomorrow}
                language={match[1]}
                PreTag="div"
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          // Disable images for security
          img: () => null,
          // Tables get a scroll container; it is only styled under .markdown-chat
          // (FE-5-19), so other surfaces render as before.
          table: ({ node, ...props }) => (
            <div className="markdown-table-scroll">
              <table {...props} />
            </div>
          ),
        }}
        // Disable HTML to prevent XSS
        disallowedElements={['script', 'iframe', 'object', 'embed']}
      >
        {markdownContent}
      </ReactMarkdown>
    </div>
  );
};

MarkdownRenderer.propTypes = {
  content: PropTypes.string.isRequired,
  className: PropTypes.string,
  breaks: PropTypes.bool,
};

export default MarkdownRenderer;